/**
 * PreviewFooter — Site footer with brand, nav, legal, contact columns
 */
import { Icons } from '@/data/icons';

interface PreviewFooterProps {
  data: {
    companyName?: string;
    tagline?: string;
    linkedin?: string;
    facebook?: string;
    instagram?: string;
    address?: string;
    phone?: string;
    email?: string;
  };
  settings: {
    generateAngebot?: boolean;
    generateMethoden?: boolean;
    generateUeberMich?: boolean;
    generateImpressum?: boolean;
    generateDatenschutz?: boolean;
    generateAgb?: boolean;
    [key: string]: any;
  };
}
export function PreviewFooter({ data, settings }: PreviewFooterProps) {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="preview-footer">
      <div className="preview-container">
        <div className="preview-footer__grid">
          {/* Brand Column */}
          <div className="preview-footer__brand">
            <div className="preview-footer__logo">
              <div className="preview-footer__logo-icon">
                {data.companyName?.substring(0, 2).toUpperCase()}
              </div>
              <span className="preview-footer__logo-text">
                {data.companyName?.split(" ")[0]}
              </span>
            </div>
            <p className="preview-footer__tagline">{data.tagline}</p>

            {/* Social Links */}
            <div className="preview-footer__social">
              {data.linkedin && (
                <a
                  href={data.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="LinkedIn"
                >
                  <span
                    dangerouslySetInnerHTML={{ __html: Icons.linkedin }}
                  />
                </a>
              )}
              {data.facebook && (
                <a
                  href={data.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Facebook"
                >
                  <span
                    dangerouslySetInnerHTML={{ __html: Icons.facebook }}
                  />
                </a>
              )}
              {data.instagram && (
                <a
                  href={data.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram"
                >
                  <span
                    dangerouslySetInnerHTML={{ __html: Icons.instagram }}
                  />
                </a>
              )}
            </div>
          </div>

          {/* Links Column 1 */}
          <div className="preview-footer__column">
            <h4>Navigation</h4>
            <ul>
              <li>
                <a href="index.html">Startseite</a>
              </li>
              {settings.generateAngebot && (
                <li><a href="angebot.html">Angebot</a></li>
              )}
              {settings.generateMethoden && (
                <li><a href="methoden.html">Methoden</a></li>
              )}
              {settings.generateUeberMich && (
                <li><a href="ueber-mich.html">Über mich</a></li>
              )}
              <li>
                <a href="#kontakt">Kontakt</a>
              </li>
            </ul>
          </div>

          {/* Links Column 2 — only if legal pages exist */}
          {(settings.generateImpressum || settings.generateDatenschutz || settings.generateAgb) && (
          <div className="preview-footer__column">
            <h4>Rechtliches</h4>
            <ul>
              {settings.generateImpressum && (
                <li><a href="impressum.html">Impressum</a></li>
              )}
              {settings.generateDatenschutz && (
                <li><a href="datenschutz.html">Datenschutz</a></li>
              )}
              {settings.generateAgb && (
                <li><a href="agb.html">AGB</a></li>
              )}
            </ul>
          </div>
          )}

          {/* Contact Column */}
          <div className="preview-footer__column">
            <h4>Kontakt</h4>
            <ul>
              <li
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "8px",
                }}
              >
                <span
                  dangerouslySetInnerHTML={{ __html: Icons.mapPin }}
                  style={{
                    flexShrink: 0,
                    marginTop: "2px",
                    opacity: 0.6,
                  }}
                />
                <span>{data.address}</span>
              </li>
              <li>
                <a
                  href={`tel:${data.phone?.replace(/\s/g, "")}`}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <span
                    dangerouslySetInnerHTML={{ __html: Icons.phone }}
                    style={{ opacity: 0.6 }}
                  />
                  {data.phone}
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${data.email}`}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <span
                    dangerouslySetInnerHTML={{ __html: Icons.mail }}
                    style={{ opacity: 0.6 }}
                  />
                  {data.email}
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="preview-footer__bottom">
          <span>
            &copy; {currentYear} {data.companyName}. Alle Rechte vorbehalten.
          </span>
          <div className="preview-footer__legal">
            {settings.generateImpressum && <a href="impressum.html">Impressum</a>}
            {settings.generateDatenschutz && <a href="datenschutz.html">Datenschutz</a>}
          </div>
        </div>
      </div>
    </footer>
  );
}
